import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FiBox, FiCheckCircle, FiEdit3, FiRotateCcw, 
  FiAlertCircle, FiClipboard, FiActivity 
} from "react-icons/fi";

// Components
import MasterSideBar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import InventoryTabs from "../../components/operationsAssociate/InventoryTabs";
import MyItemsTable from "../../components/operationsAssociate/MyItemsTable";
import ReportUsageModal from "../../components/operationsAssociate/ReportUsageModal";

const API_URL = "https://ims.holdthechild.net";

const MyInventoryPage = () => {
    // --- UI State ---
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Data State ---
    const [myItems, setMyItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);

    const fetchMyItems = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");

            const response = await axios.get(`${API_URL}/api/issued-items/my-items`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMyItems(response.data.data || []);
        } catch (err) {
            setError("Personal ledger sync offline.");
            toast.error("Failed to load your inventory ❌");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchMyItems(); }, []);

    // --- Actions ---
    const handleConfirmReceipt = async (itemId) => {
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_URL}/api/issued-items/confirm-receipt/${itemId}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Receipt Confirmed ✅");
            fetchMyItems();
        } catch (error) {
            toast.error("Confirmation failed ❌");
        }
    };

    const handleReturnItem = async (itemId) => {
        if (!window.confirm("CONFIRM RETURN TO WAREHOUSE?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_URL}/api/issued-items/return/${itemId}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Item Returned ✅");
            fetchMyItems();
        } catch (error) {
            toast.error("Return protocol failed ❌");
        }
    };

    // --- UI Components ---
    const PersonalUsageMatrix = () => (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
                { label: "Items in Hand", val: myItems.length, icon: FiBox, color: "text-blue-600", bg: "bg-blue-50" },
                { label: "Pending Receipt", val: myItems.filter(i => !i.confirmed).length, icon: FiAlertCircle, color: "text-orange-600", bg: "bg-orange-50" },
                { label: "Confirmed Stock", val: myItems.filter(i => i.confirmed).length, icon: FiCheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
            ].map((stat, i) => (
                <div key={i} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1">{stat.label}</p>
                        <p className={`text-2xl font-black ${stat.color}`}>{stat.val}</p>
                    </div>
                    <div className={`${stat.bg} p-4 rounded-2xl`}>
                        <stat.icon className={stat.color} size={20} />
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
            <MasterSideBar 
                visible={sidebarOpen} 
                onClose={() => setSidebarOpen(false)} 
                collapsed={sidebarCollapsed} 
                setCollapsed={setSidebarCollapsed} 
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-7xl mx-auto space-y-6">
                        
                        <div className="bg-white p-2 rounded-3xl shadow-sm border border-gray-100 inline-block mb-4">
                            <InventoryTabs />
                        </div>

                        {/* Header Section */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                                    Issued <span className="text-orange-600">Stock</span>
                                </h1>
                                <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mt-1 italic">Consumables in your custody</p>
                            </div>
                            
                            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-gray-100 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                <FiActivity className="text-emerald-500" /> Usage Tracking Active
                            </div>
                        </div>

                        <PersonalUsageMatrix />

                        

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[3rem] border border-gray-100 shadow-sm">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mb-4"></div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Retrieving Your Item Buffer...</p>
                            </div>
                        ) : error ? (
                            <div className="p-12 text-center bg-red-50 rounded-[3rem] border border-red-100">
                                <p className="text-red-600 font-black uppercase tracking-widest mb-4 italic">{error}</p>
                                <button onClick={fetchMyItems} className="bg-red-600 text-white px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">Retry Sync</button>
                            </div>
                        ) : (
                            <motion.div 
                                initial={{ opacity: 0, y: 10 }} 
                                animate={{ opacity: 1, y: 0 }} 
                                className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-6"
                            >
                                <MyItemsTable
                                    items={myItems}
                                    onConfirmReceipt={handleConfirmReceipt}
                                    onReportUsage={(item) => {
                                        setSelectedItem(item);
                                        setIsUsageModalOpen(true);
                                    }}
                                    onReturnItem={handleReturnItem}
                                />
                            </motion.div>
                        )}
                    </div>
                </main>
            </div>

            {/* --- Modals --- */}
            <AnimatePresence>
                {isUsageModalOpen && selectedItem && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }} 
                            animate={{ scale: 1, opacity: 1 }} 
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden"
                        >
                            <ReportUsageModal
                                closeModal={() => {
                                    setIsUsageModalOpen(false);
                                    setSelectedItem(null);
                                    fetchMyItems();
                                }}
                                item={selectedItem}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MyInventoryPage;